// @ts-strict-ignore
/**
 * Created by aaralikar on 1/12/2018.
 */
import { Injectable, ViewContainerRef } from '@angular/core';
import {
  SohoContextualActionPanelRef,
  SohoContextualActionPanelService,
} from 'ids-enterprise-ng';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { LMDataService } from '@services/lm-data.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMActionFormDialogResult, LMActionFormDialogResultCode } from './lm-action-form-dialog.service';
import { LMActionFormDialogComponent } from './lm-action-form-dialog.component';
import { LMTransactionState } from '@transaction/lm-transaction-state';
import { LMActionRequestSaveMode, LMActionRequestStatus, LMDialogButtonKey, LMUniqueIdService } from '@utils/index';
import { LMScheduleDialogService } from './print-dialog';
import { filter } from 'rxjs/operators';
import { LMThemeService } from '@banner/lm-theme.service';
import { LMMessageWrapperDialogRef, LMModalWrapperService } from '@app/services';

@Injectable()
export class LMContextualActionPanelService {

  constructor(
    private sohoContextualActionPanelService: SohoContextualActionPanelService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmDataService: LMDataService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmLoggerService: LMLoggerService,
    private lmScheduleDialogService: LMScheduleDialogService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmThemeService: LMThemeService,
    private lmModalWrapperService: LMModalWrapperService,
  ) {}

  private openPanels: Array<SohoContextualActionPanelRef<LMActionFormDialogComponent>> = [];
  private scheduleView: Array<LMDataView> = [];
  public messageDialog: LMMessageWrapperDialogRef;

  public get hasOpenDialog(): boolean {
    return this.openPanels.length > 0;
  }

  public get getOpenDialog(): SohoContextualActionPanelRef<LMActionFormDialogComponent> {
    return this.hasOpenDialog ? this.openPanels[this.openPanels.length - 1] : undefined;
  }

  public getContextualPanelDataView(formDataUrl: string): Observable<LMDataView> {
    return this.lmDataService.getRecord(formDataUrl);
  }

  public openContextualActionPanel(formSpec: LMFormSpec, actionSpec: LMActionSpec, placeholder: ViewContainerRef,
    dataView?: LMDataView, transactionState?: LMTransactionState, actionFormOption?: LMActionFormOptions): Observable<LMActionFormDialogResult> {
    return new Observable<LMActionFormDialogResult>(observer => {
      this.lmLoggerService.log(['LMContextualActionPanelService', 'openContextualActionPanel', 'begin']);
      // actionDialog$ subject is there to facilitate communication between the form component and dialog frame.
      const actionDialog$: Subject<LMActionFormDialogResult> = new Subject<LMActionFormDialogResult>();
      // Action form dialog pushes the result onAfterClose and the transaction service observer will get the result.
      // do not have to push the transaction observer here
      // actionDialog$.observers.push(observer);

      const capFormSpec: LMFormSpec = JSON.parse(JSON.stringify(formSpec));
      capFormSpec.container = 'contextualpanel';

      this.lmBusyIndicatorService.hideBusyIndicator();

      const contextualPanel = this.sohoContextualActionPanelService
        .contextualactionpanel<LMActionFormDialogComponent>(LMActionFormDialogComponent, placeholder);

      const buttons = this.getDialogButtons(dataView, formSpec, actionSpec, contextualPanel, actionDialog$, transactionState);

      contextualPanel
        .buttons(buttons)
        .title(this.getDialogTitle(capFormSpec.busFormSpec.title, dataView))
        .initializeContent(true)
        .trigger('immediate')
        .open()
        .apply((actionPanel: LMActionFormDialogComponent) => {
          actionPanel.formSpec = capFormSpec;
          actionPanel.dataView = dataView;
          actionPanel.formOptions = {actionFormDialogData: {actionSpec, actionDialog$}};

          // Need to trigger change detection manually for Change Password dialog, revisit this in the future if there is an alternative way
          actionPanel.triggerDetectChanges();
        })
      // *** beforeClose does not exist for contextual panel
      // .beforeClose( (ref) => {
      //   // if ref.dialogResult is undefined, the escape key was used
      //   const isEscapeKey = (!ref.dialogResult);
      //
      //   // TODO: restore the check back when beforeClose() is fixed
      //   // dataView indicates a Cancel key is on the dialog; ref.dialogResult.result is the button used
      //   if (dataView && (isEscapeKey/* || ref.dialogResult.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL*/)) {
      //     let model = dialog.componentDialog.formComponent.model;
      //     if (model.isDirty) {
      //       // TODO: would be nice to use dialog.componentDialog.formComponent.displayUnsavedChanges() here
      //       // TODO: restore when beforeClose() is fixed
      //       // this.displayUnsavedChanges().then((allowNavigation: boolean) => {
      //       //   return allowNavigation;
      //       // });
      //       return false;
      //     }
      //   }
      //   return true;
      // })
        .afterClose(result => {
          observer.next(result);
          observer.complete();
        // actionDialog$.complete(); // Commented this line out due to multiple toast messages displaying.
        // Still need to figure out issue on check control not being unchecked after doing a Cancel - LMCLIENT-25672
        })
        .afterOpen(() => {
          this.openPanels.push(contextualPanel);
          this.scheduleView.push(<LMDataView>{});
        });
    });
  }

  private getDialogTitle(titleMsgRule: LMMessageRule, dataView: LMDataView): string {
    if (titleMsgRule && dataView) {
      return this.lmFormatUtilService.formatMessageRule(titleMsgRule, dataView);
    }
    // TODO: Default message
    return Soho.Locale.translate('ActionForm');
  }

  /* tslint:disable-next-line:max-line-length */
  private dialogClose(contextualPanel: SohoContextualActionPanelRef<LMActionFormDialogComponent>, actionDialog$: Subject<LMActionFormDialogResult>, result: LMActionFormDialogResult) {
    actionDialog$.complete(); // UnCommented this line out due to multiple toast messages displaying.
    // Still need to figure out issue on check control not being unchecked after doing a Cancel - LMCLIENT-25672
    this.openPanels.pop();
    this.scheduleView.pop();
    contextualPanel.close(result);
  }

  /* tslint:disable-next-line:max-line-length */

  private getDialogButtons(dataView: LMDataView, formSpec: LMFormSpec, actionSpec: LMActionSpec,
    contextualPanel: SohoContextualActionPanelRef<LMActionFormDialogComponent>,
    actionDialog$: Subject<LMActionFormDialogResult>, transactionState?: LMTransactionState): SohoContextualActionPanelButton[] {
    const actionRequestArray = (transactionState) ? transactionState.transactionRequestList : undefined;
    let renderAllPfActions = true;
    const current = (Soho as any).breakpoints.current();
    if (current === 'phone' || current === 'phonedown' ) {
      // Render cancel, approve and reject only in mobile for process flow action requests.
      // Other action dialog forms will have cancel and submit
      renderAllPfActions = (dataView.processFlowActions && dataView.processFlowActions.length > 0) ? false : true;
    }

    const formNamePrefix = (formSpec.busFormSpec.namePrefix) ? formSpec.busFormSpec.namePrefix : '';
    const formName = formSpec.busFormSpec.name;
    let buttons: SohoContextualActionPanelButton[] = [];
    // if there is no action just present a close button
    // this happens when a list opens a record in
    // read only mode (no valid update action)
    // TODO: SohoContextualActionPanelButton does not have an id property
    if (!actionSpec) {
      buttons.push({
        // id:    this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.CLOSE ]),
        text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
        click: (e, modal) => {
          this.dialogClose(contextualPanel, actionDialog$, <LMActionFormDialogResult>{
            result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL
          });
        }
      });

      return buttons;
    }

    if (dataView) {
      // 1. Cancel Button
      buttons.push({
        // id:    this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.CANCEL ]),
        text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
        click: (e, modal) => {
          // TODO: would be nice to use dialog.componentDialog.formComponent.displayUnsavedChanges() here
          // but the message does not always display
          const model = contextualPanel.componentPanel.formComponent.model;
          if (model.isDirty) {
            this.displayUnsavedChanges().then((allowNavigation: boolean) => {
              if (allowNavigation) {
                this.dialogClose(contextualPanel, actionDialog$, <LMActionFormDialogResult>{
                  result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL
                });
              }
            });
          } else {
            this.dialogClose(contextualPanel, actionDialog$, <LMActionFormDialogResult>{
              result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL
            });
          }
          // this.dialogClose(contextualPanel, actionDialog$, <LMActionFormDialogResult>{ result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL });
        }
      });

      // 2. Compare button
      if (dataView.canCompare && renderAllPfActions) {
        buttons.push({
          // id:    this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.COMPARE ]),
          text: Soho.Locale.translate(LMDialogButtonKey.COMPARE),
          icon: '#icon-' + this.lmThemeService.lplIconToSohoXI('process'),
          click: (e, modal) => {
            const event = e;
            this.subscribeToCAPButtonClick(event, contextualPanel, actionDialog$);
            actionDialog$.next(<LMActionFormDialogResult>{ result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_COMPARE });
          }
        });
      }

      // 3. Process flow actions
      if (dataView.processFlowActions && dataView.processFlowActions.length > 0) {
        const pfActions =  dataView.processFlowActions;
        for (let i = 0; i < pfActions.length; i++) {
          const pfAction = pfActions[i];
          if (!renderAllPfActions) {
            if (pfAction.name.toLowerCase() !== 'approve'
              && pfAction.name.toLowerCase() !== 'reject') {
              continue;
            }
          }
          let iconName: LMIcon = 'process';
          if (pfAction.name.toLowerCase() === 'approve') {
            iconName = <LMIcon> 'approve';
          } else if (pfAction.name.toLowerCase() === 'acknowledge') {
            iconName = <LMIcon> 'submit';
          } else if (pfAction.name.toLowerCase() === 'reject') {
            iconName = <LMIcon> 'reject';
          } else if (pfAction.name.toLowerCase() ===  'return') {
            iconName = <LMIcon> 'undo';
          }
          buttons.push({
            // id:    this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, pfAction.label ]),
            text: pfAction.label,
            icon: '#icon-' + this.lmThemeService.lplIconToSohoXI(iconName),
            click: (e, modal) => {
              const event = e;
              this.subscribeToCAPButtonClick(event, contextualPanel, actionDialog$);
              actionDialog$.next(<LMActionFormDialogResult>{ result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_PFACTION, pfActionName: pfAction.name });
            }
          });
        }
      }

      if (actionSpec.willRunAsynchronous) {
        buttons.push({
          // id:    this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.SCHEDULE ]),
          text: Soho.Locale.translate(LMDialogButtonKey.SCHEDULE),
          click: (e, modal) => {
            const event = e;
            this.lmScheduleDialogService.openScheduleDialog(dataView.busClassName, actionSpec.name)
              .subscribe((scheduleView: LMDataView) => {
                this.scheduleView[this.scheduleView.length - 1] = scheduleView;
              });
            this.subscribeToCAPButtonClick(event, contextualPanel, actionDialog$);
          }
        })
      }

      // 4. Ok Button (Save as draft)
      if (!dataView.displayOnly && renderAllPfActions) {
        let okText = LMDialogButtonKey.SUBMIT;
        let okText2;
        let pfActionRequestSaveMode: LMActionRequestSaveMode;
        let pfActionRequestSaveMode2: LMActionRequestSaveMode;
        const defaultButton = true; // (dataView.processFlowActions && dataView.processFlowActions.length > 0) ? false : true;

        const isInitiator = dataView.contextProperties && dataView.contextProperties[ 'context_prd_initiator' ] !== undefined && dataView.contextProperties[ 'context_prd_initiator' ] === 'true';
        const isActionRequest = dataView.contextProperties && dataView.contextProperties[ 'context_prd_action_prd_request' ] !== undefined
          && dataView.contextProperties[ 'context_prd_action_prd_request' ].length > 0;
        const isApprover = dataView.contextProperties && dataView.contextProperties[ 'context_prd_approver' ] !== undefined && dataView.contextProperties[ 'context_prd_approver' ] === 'true';
        const isFinalApprover = dataView.contextProperties && dataView.contextProperties[ 'context_prd_finalapprover' ] !== undefined
          && dataView.contextProperties[ 'context_prd_finalapprover' ] === 'true';

        if (isInitiator) {
          if (isActionRequest) {
            // Action request is already created.
            okText = LMDialogButtonKey.SAVEASDRAFT;
            pfActionRequestSaveMode = LMActionRequestSaveMode.DRAFT;
            const notInProcess = (dataView.contextProperties[ 'context_prd_action_prd_request_prd_status' ]
              && dataView.contextProperties[ 'context_prd_action_prd_request_prd_status' ] === LMActionRequestStatus.NOTINPROCESS)
            if (notInProcess) {
              okText2 = LMDialogButtonKey.SUBMIT;
              pfActionRequestSaveMode2 = LMActionRequestSaveMode.SUBMIT;
            }
          } else {
            okText = LMDialogButtonKey.SUBMIT;
            pfActionRequestSaveMode = LMActionRequestSaveMode.SUBMIT;
            okText2 = LMDialogButtonKey.SAVEASDRAFT;
            pfActionRequestSaveMode2 = LMActionRequestSaveMode.DRAFT;
          }
        } else if (isApprover || isFinalApprover) {
          okText = LMDialogButtonKey.SAVEASDRAFT;
          pfActionRequestSaveMode = LMActionRequestSaveMode.DRAFT;
        }

        if (okText2) {
          buttons.push({
            // id:       this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, okText2 ]),
            text:     Soho.Locale.translate(okText2),
            validate: false,
            click: (e, modal) => {
              const event = e;
              this.subscribeToCAPButtonClick(event, contextualPanel, actionDialog$);
              const scheduleDataView = this.scheduleView[this.scheduleView.length - 1];
              actionDialog$.next(<LMActionFormDialogResult>{
                result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OK2,
                pfActionRequestSaveMode: pfActionRequestSaveMode2,
                actionRequestArray,
                transactionStateResult: transactionState,
                scheduleView: scheduleDataView.fields ? scheduleDataView : undefined
              });
            }
          });
        }

        if (okText) {
          buttons.push({
            // id:        this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, okText ]),
            text:      Soho.Locale.translate(okText),
            isDefault: defaultButton,
            validate : false,
            click: (e, modal) => {
              const event = e;
              this.subscribeToCAPButtonClick(event, contextualPanel, actionDialog$);
              const scheduleDataView = this.scheduleView[this.scheduleView.length - 1];
              actionDialog$.next(<LMActionFormDialogResult>{
                result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OK,
                pfActionRequestSaveMode,
                actionRequestArray,
                transactionStateResult: transactionState,
                scheduleView: scheduleDataView.fields ? scheduleDataView : undefined
              });
            }
          });
        }
      }
    } else {
      buttons = [
        // TODO: The button default disables the button. A bug in controls?
        // tslint:disable-next-line - shadowed variable e
        {
          // id:    this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.CANCEL ]),
          text:  Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: (e, modal) => {
            this.dialogClose(contextualPanel, actionDialog$, <LMActionFormDialogResult>{ result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL });
          }
        },
        {
          // id:        this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.SUBMIT ]),
          text: Soho.Locale.translate(LMDialogButtonKey.SUBMIT),
          isDefault: true,
          validate : false,
          click: (e, modal) => {
            actionDialog$.next(<LMActionFormDialogResult>{ result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OK });
            actionDialog$.subscribe((result: LMActionFormDialogResult) => {
              if (result.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED) {
                this.dialogClose(contextualPanel, actionDialog$, result);
              }
            });
          }
        }
      ];
    }
    return buttons;
  }

  private subscribeToCAPButtonClick(event: any, panel: SohoContextualActionPanelRef<LMActionFormDialogComponent>, actionDialog$: Subject<LMActionFormDialogResult>) {
    this.disableButtons(event);
    // With first(), an error is thrown when subject completes (Emptyerror no elements in sequence).
    // Cannot use take(1) because the message intended to go to the form also comes here.
    // Do not cancel the subscription when the result is OK, OK2, PFACTION, COMPARE. Wait for saved or enablebuttons.
    const subscription = actionDialog$.subscribe((result: LMActionFormDialogResult) => {
      if (result.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED) {
        this.dialogClose(panel, actionDialog$, result);
        subscription.unsubscribe();
      } else if (result.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_ENABLEBUTTONS) {
        this.enableButtons(event);
        subscription.unsubscribe();
      }
    });
  }

  // TODO: would be nice to use dialog.componentDialog.formComponent.displayUnsavedChanges() but the message does not always display
  private displayUnsavedChanges(message?: string): Promise<any> {
    const youMayNavigateSource = new BehaviorSubject<boolean>(false);
    const youMayNavigate: Observable<boolean> = youMayNavigateSource.asObservable();

    const dialogId = 'UnsavedChanges';
    const buttons = [
      {
        id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.LEAVE ]),
        text:  Soho.Locale.translate(LMDialogButtonKey.LEAVE),
        click: (e, modal) => {
          youMayNavigateSource.next(true);
          youMayNavigateSource.complete();
          modal.close();
        },
      },
      {
        id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.STAY ]),
        text:  Soho.Locale.translate(LMDialogButtonKey.STAY),
        click: (e, modal) => {
          youMayNavigateSource.next(false);
          youMayNavigateSource.complete();
          modal.close();
        },
      }
    ];

    const title = Soho.Locale.translate('UnsavedChangesMessage');
    if (!message) {
      message = '<p>' + Soho.Locale.translate('DocumentHasChangesMessage') + '</p>'
        + '<p>' + Soho.Locale.translate('ClickStayToSaveChanges') + '</p>'
        + '<p>' + Soho.Locale.translate('ClickLeaveToIgnoreChanges') + '</p>';
    }

    this.messageDialog = this.lmModalWrapperService
      .message()
      .message(message)
      .allowedTags('<p>')
      .title(title)
      .buttons(buttons)
      .open();

    return (youMayNavigate.pipe(filter(el => el))).toPromise();
  }

  private disableButtons(e) {
    const targetElement: HTMLElement = e.currentTarget as HTMLElement;
    const parent = targetElement.parentElement;
    const children = parent.children;
    for (let index = 0, len = children.length; index < len; index++) {
      const btn = children[ index ];
      const id = btn.getAttribute('id');
      if (id.indexOf(LMDialogButtonKey.CANCEL) > -1 || id.indexOf(LMDialogButtonKey.CLOSE) > -1) {
        continue;
      }
      btn.setAttribute('disabled', 'true');
    }
  }
  private enableButtons(e) {
    const targetElement: HTMLElement = e.currentTarget as HTMLElement;
    const parent = targetElement.parentElement;
    const children = parent.children;
    for (let index = 0, len = children.length; index < len; index++) {
      const btn = children[ index ];
      const id = btn.getAttribute('id');
      if (id.indexOf(LMDialogButtonKey.CANCEL) > -1 || id.indexOf(LMDialogButtonKey.CLOSE) > -1) {
        continue;
      }
      btn.removeAttribute('disabled');
    }
  }
}
